import React from "react";
import { MATERIALS } from "../../constants";
import { w2pt, w2p, izoom, mm2m } from "../../utils/coords";
import { Geo } from "../../utils/geo";

export default function WallLayer({ st, selEdgeId, onSelect, showHE, dragNodeId, dragPosMm, zoom, selManyEdges, dragNodes, dragDelta, trimTarget }) {
  const s = izoom(zoom);
  const wallsLayer = (st.layers || {}).walls;
  if (wallsLayer?.visible === false) return null;
  // [T2] collect broken edge IDs for highlight
  // Live positions for multi-drag nodes
  const multiDragMap = dragNodes && dragDelta
    ? Object.fromEntries(dragNodes.map(({ id, x0, y0 }) => [
        id, { x: x0 + (dragDelta.dx || 0), y: y0 + (dragDelta.dy || 0) }
      ]))
    : null;

  const brk = new Set(
    (st._errors || []).filter(e => ["A", "B", "C"].includes(e.rule)).flatMap(e => e.ids || [])
  );

  return (
    <g>
      {Object.values(st.edges).map(e => {
        const ab = st.halfEdges[e.halfEdgeA], ba = st.halfEdges[e.halfEdgeB];
        if (!ab || !ba) return null;
        let wA = st.nodes[ab.originId], wB = st.nodes[ba.originId];
        if (!wA || !wB) return null;
        if (dragNodeId && dragPosMm) {
          if (ab.originId === dragNodeId) wA = dragPosMm;
          if (ba.originId === dragNodeId) wB = dragPosMm;
        }
        // Multi-node drag: override positions from multiDragMap
        if (multiDragMap) {
          if (multiDragMap[ab.originId]) wA = multiDragMap[ab.originId];
          if (multiDragMap[ba.originId]) wB = multiDragMap[ba.originId];
        }

        const pA = w2pt(wA), pB = w2pt(wB);
        const dx = pB.x - pA.x, dy = pB.y - pA.y, L = Math.sqrt(dx * dx + dy * dy);
        if (L < 0.5) return null;

        const isSel    = e.id === selEdgeId;
        const isMultiSel = (selManyEdges || []).includes(e.id);
        const isBrk    = brk.has(e.id);
        const isTrimTarget = e.id === trimTarget;
        const mat = MATERIALS[e.materialId || "BRICK"];
        const T = w2p(e.thickness || 230) / 2;
        const nx = -dy / L, ny = dx / L;
        const poly = [
          `${pA.x + nx * T},${pA.y + ny * T}`,
          `${pB.x + nx * T},${pB.y + ny * T}`,
          `${pB.x - nx * T},${pB.y - ny * T}`,
          `${pA.x - nx * T},${pA.y - ny * T}`,
        ].join(" ");

        const mx = (pA.x + pB.x) / 2, my = (pA.y + pB.y) / 2;
        let ang = Math.atan2(dy, dx) * 180 / Math.PI;
        if (ang > 90 || ang < -90) ang += 180;

        const wallC = mat?.color || "#555";
        const selC  = "#e8a838";
        const baseStroke = isBrk ? "#f87171" : isTrimTarget ? "#fb923c" : isSel ? selC : isMultiSel ? "#3b82f6" : wallC + "99";
        const colAB = ab.faceId ? "#60a5fa" : "#1a2a3a";
        const colBA = ba.faceId ? "#a78bfa" : "#1a2a3a";
        const off = s * 10, aAng = Math.atan2(dy, dx) * 180 / Math.PI;
        const arAx = mx + nx * off, arAy = my + ny * off;
        const arBx = mx - nx * off, arBy = my - ny * off;

        return (
          <g key={e.id} style={{ cursor: "pointer" }} onClick={ev => { ev.stopPropagation(); onSelect(e.id); }}>
            <line x1={pA.x} y1={pA.y} x2={pB.x} y2={pB.y} stroke="transparent" strokeWidth={s * 18} />
            <polygon
              points={poly}
              fill={isBrk ? "#2a080810" : isTrimTarget ? "#fb923c18" : isSel ? selC + "12" : isMultiSel ? "#3b82f615" : wallC + "18"}
              stroke={baseStroke}
              strokeWidth={isSel ? s * 1.5 : isTrimTarget ? s * 1.2 : isMultiSel ? s * 0.8 : s * 0.4}
              strokeDasharray={isBrk ? `${s * 4},${s * 2}` : isTrimTarget ? `${s * 5},${s * 2}` : undefined}
            />
            {/* Endpoint connection dots — tiny, shows where walls join */}
            <circle cx={pA.x} cy={pA.y} r={s * 1.8}
              fill={isSel ? selC : "#3b82f640"}
              stroke={isSel ? selC : "#3b82f620"}
              strokeWidth={s * 0.3}
              style={{ pointerEvents:"none" }}/>
            <circle cx={pB.x} cy={pB.y} r={s * 1.8}
              fill={isSel ? selC : "#3b82f640"}
              stroke={isSel ? selC : "#3b82f620"}
              strokeWidth={s * 0.3}
              style={{ pointerEvents:"none" }}/>

            {showHE && L > s * 30 && (
              <>
                <text x={arAx} y={arAy} textAnchor="middle" dominantBaseline="middle"
                  fontSize={s * 11} fill={colAB} fontFamily="monospace" style={{ pointerEvents: "none" }}
                  transform={`rotate(${aAng},${arAx},${arAy})`}>›</text>
                <text x={arBx} y={arBy} textAnchor="middle" dominantBaseline="middle"
                  fontSize={s * 11} fill={colBA} fontFamily="monospace" style={{ pointerEvents: "none" }}
                  transform={`rotate(${aAng + 180},${arBx},${arBy})`}>›</text>
              </>
            )}
            {L > s * 40 && (
              <text x={mx} y={my - s * 14} textAnchor="middle" fontSize={s * 7}
                fill={isSel ? selC : wallC + "cc"} fontFamily="monospace" style={{ pointerEvents: "none" }}
                transform={`rotate(${ang},${mx},${my - s * 10})`}>
                {Geo.d(wA, wB) >= 1000 ? mm2m(Geo.d(wA, wB)) + "m" : Math.round(Geo.d(wA, wB)) + "mm"}
              </text>
            )}
          </g>
        );
      })}
    </g>
  );
}
